/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Fragment, MouseEvent, useState } from "react";

import { useTranslation } from "react-i18next";

import { noop } from "lodash";
import ActivateIcon from "@/assets/customIcons/actions/ActivateIcon";
import DeactivateIcon from "@/assets/customIcons/actions/DeactivateIcon";
import { useDrawerStore } from "@/stores/useDrawerStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import {
  ArrowBackIcon,
  CalendarQuickIcon,
  CalendarRemoveIcon,
  DeleteOutlineIcon,
  ExpandMoreIcon,
  MoveFilesIcon
} from "@dotnet/telematics-design-system";

import { useAuth } from "../../../../context/Auth/AuthProvider";
import { USER_PERMISSIONS } from "../../../../roles/types/enums";
import {
  ANALYSIS_PROVIDERS_ACTIONS,
  COMPANIES_ACTIONS,
  DRIVERS_ACTIONS,
  INTEGRATIONS_ACTIONS,
  MenuAnchorEl,
  ROUTES,
  TableRowDataType,
  useMediaQueries,
  USERS_ACTIONS,
  VEHICLES_ACTIONS
} from "../../../../shared";
import ListChoice from "../../../ListChoice/ListChoice";
import Menu from "../../../Menu/Menu";
import { useDialog } from "../../../PageHeader/actions/hooks/useDialog";
import { Actions } from "../../../PageHeader/actions/types/types";
import { deselectAllRows } from "../../functions";
import ActionsForSelectionDialog from "../actionsForSelection/ActionsForSelectionDialog";
import { useStyles } from "../styles";
import ActionButton from "./ActionButton";
import ActionListChoice from "./ActionListChoice";

interface ToolbarProps {
  route: ROUTES;
  resourceActions: Actions[] | null;
  toggleActiveHead: (value: boolean) => void;
}

const SharedToolbar = ({
  route,
  resourceActions,
  toggleActiveHead
}: ToolbarProps) => {
  const { getAccessStatus } = useAuth();
  const { t } = useTranslation();
  const { isDialogOpen, openDialog, closeDialog } = useDialog();
  const { toMd, fromMd, to1099, toLg } = useMediaQueries();

  const {
    selectedRows,
    selectableRows,
    setSelectedRows,
    params: { desktopDetailsSection }
  } = useTableStoreByRoute(route);

  const isDesktopDrawerOpen = useDrawerStore(
    state => state.isDesktopDrawerOpen
  );

  const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

  const { sharedToolbarActionMenu, sharedToolbarActionListChoice } =
    useStyles();

  const atLeastSingleSelectedHasChildCompanies = selectedRows.some(
    (rowId: string) =>
      selectableRows
        .map((row: TableRowDataType) => {
          return row.childCompanies?.length > 0 && row.id;
        })
        .includes(rowId)
  );

  const companiesActionToBeDisabled = COMPANIES_ACTIONS.DeleteCompany;
  const disableActionCase = atLeastSingleSelectedHasChildCompanies;
  const usersActionToDisable = USERS_ACTIONS.DeleteUser;
  const usersDisabledActionCase = !getAccessStatus(
    USER_PERMISSIONS.DeleteUsers
  );

  const disabledAction = (action: Actions) => {
    const companiesDisabledAction =
      action === companiesActionToBeDisabled && disableActionCase;

    const usersDisabledAction =
      action === usersActionToDisable && usersDisabledActionCase;

    return companiesDisabledAction || usersDisabledAction;
  };

  const deleteCompanyButtonTooltip = (action: Actions) =>
    action === companiesActionToBeDisabled &&
    t("Tooltip##delete company action");

  const deleteUsersButtonTooltip = (action: Actions) =>
    action === usersActionToDisable && t("Tooltip##no action permission");

  const shouldActionButtonsHaveOnlyIcon = () => {
    if (route === ROUTES.Vehicles || route === ROUTES.Drivers) {
      return toLg || isDesktopDrawerOpen || !!desktopDetailsSection;
    }

    if (isDesktopDrawerOpen && desktopDetailsSection) {
      if (toLg) {
        return true;
      }
    }

    if (fromMd && to1099 && desktopDetailsSection) {
      return true;
    }
  };

  const actionTooltipTitle = (action: Actions) =>
    deleteCompanyButtonTooltip(action) ||
    deleteUsersButtonTooltip(action) ||
    "";

  const handleCloseMenu = () => setAnchorEl(null);

  const handleOpenMenu = (e: MouseEvent<HTMLDivElement>) =>
    setAnchorEl(e.currentTarget);

  const resetView = () => {
    toggleActiveHead(false);
    deselectAllRows(setSelectedRows);
  };

  const handleActionButtonClick = (action: Actions) =>
    disabledAction(action) ? noop : openDialog(action);

  const getIconLeft = (action: Actions) => {
    switch (action) {
      case DRIVERS_ACTIONS.BulkQuickScheduleDownload:
        return <CalendarQuickIcon key={action} />;

      case VEHICLES_ACTIONS.DeleteVehicle:
      case DRIVERS_ACTIONS.DeleteDriver:
      case USERS_ACTIONS.DeleteUser:
      case COMPANIES_ACTIONS.DeleteCompany:
      case INTEGRATIONS_ACTIONS.DeleteIntegration:
      case ANALYSIS_PROVIDERS_ACTIONS.Delete:
        return <DeleteOutlineIcon key={action} />;

      case VEHICLES_ACTIONS.MoveVehicles:
        return <MoveFilesIcon key={action} />;

      case VEHICLES_ACTIONS.QuickScheduleDownload:
        return (
          <CalendarQuickIcon
            key={action}
            style={{
              width: "22px",
              height: "22px"
            }}
          />
        );

      case VEHICLES_ACTIONS.RemoveSchedules:
      case DRIVERS_ACTIONS.RemoveSchedules:
        return <CalendarRemoveIcon key={action} />;

      case VEHICLES_ACTIONS.Activate:
        return <ActivateIcon key={action} />;

      case VEHICLES_ACTIONS.Deactivate:
        return <DeactivateIcon key={action} />;

      default:
        return <Fragment key={action}></Fragment>;
    }
  };

  return (
    <Fragment>
      {fromMd &&
        !shouldActionButtonsHaveOnlyIcon() &&
        resourceActions?.map((action: Actions) => {
          return (
            <ActionButton
              key={action}
              action={action}
              disabledAction={disabledAction(action)}
              tooltipTitle={actionTooltipTitle(action)}
              onButtonClick={() => handleActionButtonClick(action)}
            />
          );
        })}
      {(toMd || shouldActionButtonsHaveOnlyIcon()) && (
        <Fragment>
          {resourceActions === null ? (
            <div onClick={resetView}>{t("Button##default view")}</div>
          ) : (
            <Fragment>
              <div onClick={handleOpenMenu}>
                {t("Button##actions")}
                <ExpandMoreIcon
                  css={css({
                    marginLeft: "4px"
                  })}
                />
              </div>

              <Menu
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorEl={anchorEl}
                slotProps={{ list: { disableListWrap: true } }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center"
                }}
                css={css(sharedToolbarActionMenu)}
              >
                {toMd && (
                  <ListChoice
                    title={t("Button##default view")}
                    iconLeft={<ArrowBackIcon />}
                    onClick={resetView}
                    css={css(sharedToolbarActionListChoice)}
                  />
                )}
                {resourceActions.map(action => (
                  <ActionListChoice
                    key={action}
                    action={action}
                    disabledAction={disabledAction(action)}
                    tooltipTitle={actionTooltipTitle(action)}
                    onActionClick={() => handleActionButtonClick(action)}
                    getIconLeft={getIconLeft}
                  />
                ))}
              </Menu>
            </Fragment>
          )}
        </Fragment>
      )}
      {resourceActions?.map((action: Actions) => (
        <ActionsForSelectionDialog
          route={route}
          key={action}
          isOpen={isDialogOpen[action as keyof typeof isDialogOpen]}
          close={closeDialog}
          resourceAction={action}
          onDefaultView={resetView}
        />
      ))}
    </Fragment>
  );
};

export default SharedToolbar;
